import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { BillDraft, ConsultationComment, DraftingProject, DraftingProjectStatus, Legislation, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, PageHeader, Spinner, StatusBadge, Tabs } from '../../ui/components';

const STATUSES: DraftingProjectStatus[] = [
  'INSTRUCTIONS_RECEIVED',
  'DRAFTING',
  'CONSULTATION',
  'CABINET_CLEARANCE',
  'ASSEMBLY_HANDOFF',
  'ENACTED',
  'WITHDRAWN',
];

const TABS = [
  { key: 'drafts', label: 'Draft versions' },
  { key: 'comments', label: 'Consultation comments' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

export function DraftingDetail() {
  const { id } = useParams<{ id: string }>();
  const { persona } = useAuth();
  const isDrafter = persona?.roles.includes('LEGISLATIVE_DRAFTER') ?? false;
  const [tab, setTab] = useState<TabKey>('drafts');

  const projectQ = useApiQuery<DraftingProject>(id ? `/api/v1/m38/drafting-projects/${id}` : null);
  const draftsQ = useApiQuery<{ items: BillDraft[] }>(id ? `/api/v1/m38/drafting-projects/${id}/drafts` : null);

  const [assignNin, setAssignNin] = useState('');
  const [newDraftBody, setNewDraftBody] = useState('');
  const [newDraftSummary, setNewDraftSummary] = useState('');
  const [advanceTarget, setAdvanceTarget] = useState<DraftingProjectStatus | ''>('');
  const [withdrawnReason, setWithdrawnReason] = useState('');
  const [enactedLegislationId, setEnactedLegislationId] = useState('');
  const [legislationOptions, setLegislationOptions] = useState<Legislation[]>([]);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (advanceTarget === 'ENACTED') {
      api
        .get<Page<Legislation>>('/api/v1/m38/legislation?limit=200')
        .then((p) => setLegislationOptions(p.items))
        .catch(() => setLegislationOptions([]));
    }
  }, [advanceTarget]);

  const project = projectQ.data;

  const assign = async () => {
    if (!id) return;
    setBusy(true);
    setActionError(null);
    try {
      await api.post(`/api/v1/m38/drafting-projects/${id}/assign`, { assignedDrafterNin: assignNin });
      projectQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Assign failed');
    } finally {
      setBusy(false);
    }
  };

  const addDraft = async () => {
    if (!id) return;
    setBusy(true);
    setActionError(null);
    try {
      await api.post(`/api/v1/m38/drafting-projects/${id}/drafts`, { bodyText: newDraftBody, changeSummary: newDraftSummary || undefined });
      setNewDraftBody('');
      setNewDraftSummary('');
      draftsQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to add draft');
    } finally {
      setBusy(false);
    }
  };

  const circulate = async (draftId: string) => {
    if (!id) return;
    setActionError(null);
    try {
      await api.post(`/api/v1/m38/drafting-projects/${id}/drafts/${draftId}/circulate`);
      draftsQ.reload();
      projectQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Circulate failed');
    }
  };

  const advance = async () => {
    if (!id || !advanceTarget) return;
    setBusy(true);
    setActionError(null);
    try {
      await api.post(`/api/v1/m38/drafting-projects/${id}/advance`, {
        status: advanceTarget,
        withdrawnReason: advanceTarget === 'WITHDRAWN' ? withdrawnReason : undefined,
        enactedLegislationId: advanceTarget === 'ENACTED' ? enactedLegislationId || undefined : undefined,
      });
      setAdvanceTarget('');
      projectQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Advance failed');
    } finally {
      setBusy(false);
    }
  };

  if (projectQ.loading) return <Spinner label="Loading drafting project…" />;
  if (projectQ.error) return <ErrorNote message={projectQ.error} />;
  if (!project) return null;

  return (
    <div>
      <PageHeader title={project.title} subtitle={`${project.instructingMda} — instructed by ${project.instructingOfficer}`} actions={<StatusBadge status={project.status} />} />

      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}

      <div className="mb-5 card p-5">
        <h2 className="mb-2 text-sm font-bold text-slate-700">Instructions</h2>
        <p className="whitespace-pre-wrap text-sm text-slate-700">{project.instructions}</p>
        {project.assignedDrafterNin ? <p className="mt-2 text-xs text-slate-500">Assigned drafter: {project.assignedDrafterNin}</p> : null}
        {project.withdrawnReason ? <p className="mt-2 text-xs text-rose-600">Withdrawn: {project.withdrawnReason}</p> : null}
      </div>

      {isDrafter ? (
        <div className="mb-5 space-y-3">
          {project.status === 'INSTRUCTIONS_RECEIVED' ? (
            <div className="card flex flex-wrap items-end gap-2 p-4">
              <Field label="Assign to drafter (NIN)" className="!mb-0">
                <input className="field-input" value={assignNin} onChange={(e) => setAssignNin(e.target.value)} />
              </Field>
              <button className="btn-primary" onClick={assign} disabled={busy || !assignNin.trim()}>
                Assign &amp; start drafting
              </button>
            </div>
          ) : null}

          <div className="card p-4">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Advance pipeline</div>
            <div className="flex flex-wrap items-end gap-2">
              <select className="field-input !w-auto" value={advanceTarget} onChange={(e) => setAdvanceTarget(e.target.value as DraftingProjectStatus | '')}>
                <option value="">Select target status…</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s.replaceAll('_', ' ')}
                  </option>
                ))}
              </select>
              {advanceTarget === 'WITHDRAWN' ? (
                <input
                  className="field-input flex-1"
                  placeholder="Reason (required)"
                  value={withdrawnReason}
                  onChange={(e) => setWithdrawnReason(e.target.value)}
                />
              ) : null}
              {advanceTarget === 'ENACTED' ? (
                <select className="field-input flex-1" value={enactedLegislationId} onChange={(e) => setEnactedLegislationId(e.target.value)}>
                  <option value="">Enacted legislation (optional)…</option>
                  {legislationOptions.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.shortTitle} ({l.instrumentNumber})
                    </option>
                  ))}
                </select>
              ) : null}
              <button
                className="btn-secondary"
                onClick={advance}
                disabled={busy || !advanceTarget || (advanceTarget === 'WITHDRAWN' && withdrawnReason.trim().length < 3)}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <Tabs tabs={TABS} value={tab} onChange={setTab} />

      {tab === 'drafts' ? (
        <div>
          {isDrafter && project.status === 'DRAFTING' ? (
            <div className="card mb-4 space-y-2 p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Add new draft</div>
              <textarea className="field-input" rows={5} placeholder="Draft text…" value={newDraftBody} onChange={(e) => setNewDraftBody(e.target.value)} />
              <input className="field-input" placeholder="Change summary (optional)" value={newDraftSummary} onChange={(e) => setNewDraftSummary(e.target.value)} />
              <button className="btn-primary" onClick={addDraft} disabled={busy || !newDraftBody.trim()}>
                Add draft
              </button>
            </div>
          ) : null}

          {draftsQ.loading ? (
            <Spinner />
          ) : draftsQ.error ? (
            <ErrorNote message={draftsQ.error} />
          ) : (draftsQ.data?.items.length ?? 0) === 0 ? (
            <EmptyState title="No drafts yet" />
          ) : (
            <ul className="space-y-2.5">
              {draftsQ.data!.items.map((d: BillDraft) => (
                <li key={d.id} className="card p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-slate-900">Version {d.versionNumber}</span>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={d.status} />
                      {isDrafter && d.status === 'DRAFT' ? (
                        <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => circulate(d.id)}>
                          Circulate
                        </button>
                      ) : null}
                    </div>
                  </div>
                  {d.changeSummary ? <p className="mt-1 text-xs text-slate-500">{d.changeSummary}</p> : null}
                  <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{d.bodyText}</p>
                  <div className="mt-1.5 text-[11px] text-slate-400">
                    sha256 {d.contentSha256.slice(0, 16)}… · {formatDateTime(d.createdAt)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      {tab === 'comments' && id ? (
        <CommentsPanel projectId={id} projectStatus={project.status} isDrafter={isDrafter} drafts={draftsQ.data?.items ?? []} />
      ) : null}
    </div>
  );
}

function CommentsPanel({
  projectId,
  projectStatus,
  isDrafter,
  drafts,
}: {
  projectId: string;
  projectStatus: DraftingProjectStatus;
  isDrafter: boolean;
  drafts: BillDraft[];
}) {
  const { persona } = useAuth();
  const commentsQ = useApiQuery<Page<ConsultationComment>>(isDrafter ? `/api/v1/m38/drafting-projects/${projectId}/consultation-comments?limit=100` : null);
  const [billDraftId, setBillDraftId] = useState('');
  const [commenterMda, setCommenterMda] = useState(persona?.org ?? '');
  const [commentText, setCommentText] = useState('');
  const [responseDraft, setResponseDraft] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const circulated = drafts.find((d) => d.status === 'CIRCULATED');

  const lodge = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m38/drafting-projects/${projectId}/consultation-comments`, {
        billDraftId: billDraftId || circulated?.id,
        commenterMda,
        commentText,
      });
      setCommentText('');
      commentsQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to lodge comment');
    } finally {
      setBusy(false);
    }
  };

  const resolve = async (commentId: string) => {
    setError(null);
    try {
      await api.post(`/api/v1/m38/drafting-projects/${projectId}/consultation-comments/${commentId}/resolve`, {
        responseText: responseDraft[commentId] ?? '',
      });
      commentsQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Resolve failed');
    }
  };

  return (
    <div>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}

      {projectStatus === 'CONSULTATION' ? (
        <div className="card mb-4 space-y-2 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Lodge feedback</div>
          {drafts.length > 1 ? (
            <select className="field-input" value={billDraftId} onChange={(e) => setBillDraftId(e.target.value)}>
              <option value="">{circulated ? `Draft v${circulated.versionNumber} (circulated)` : 'Select draft…'}</option>
              {drafts.map((d) => (
                <option key={d.id} value={d.id}>
                  Draft v{d.versionNumber} ({d.status})
                </option>
              ))}
            </select>
          ) : null}
          <input className="field-input" placeholder="Commenting MDA" value={commenterMda} onChange={(e) => setCommenterMda(e.target.value)} />
          <textarea className="field-input" rows={3} placeholder="Comment…" value={commentText} onChange={(e) => setCommentText(e.target.value)} />
          <button className="btn-primary" onClick={lodge} disabled={busy || !commenterMda.trim() || !commentText.trim() || (!billDraftId && !circulated)}>
            Lodge comment
          </button>
        </div>
      ) : null}

      {!isDrafter ? (
        <ErrorNote message="Requires LEGISLATIVE_DRAFTER to view the full comment list." />
      ) : commentsQ.loading ? (
        <Spinner />
      ) : commentsQ.error ? (
        <ErrorNote message={commentsQ.error} />
      ) : (commentsQ.data?.items.length ?? 0) === 0 ? (
        <EmptyState title="No consultation comments yet" />
      ) : (
        <ul className="space-y-2.5">
          {commentsQ.data!.items.map((c) => (
            <li key={c.id} className="card p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-900">{c.commenterMda}</span>
                <StatusBadge status={c.resolved ? 'RESOLVED' : 'OPEN'} />
              </div>
              <p className="mt-1 text-sm text-slate-700">{c.commentText}</p>
              {c.responseText ? <p className="mt-1.5 rounded bg-slate-50 p-2 text-xs text-slate-600">Response: {c.responseText}</p> : null}
              {!c.resolved ? (
                <div className="mt-2 flex gap-2">
                  <input
                    className="field-input"
                    placeholder="Response…"
                    value={responseDraft[c.id] ?? ''}
                    onChange={(e) => setResponseDraft((r) => ({ ...r, [c.id]: e.target.value }))}
                  />
                  <button className="btn-secondary shrink-0" onClick={() => resolve(c.id)} disabled={!(responseDraft[c.id] ?? '').trim()}>
                    Resolve
                  </button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
